import { Pressable, KeyboardAvoidingView, Platform, Keyboard, Alert, TouchableOpacity, StyleSheet, type ViewStyle } from "react-native"
import { loadSchemaFormStyles } from '@utils/loadWebStyles';

const webFormClass = (className: string) =>
  Platform.OS === 'web' ? ({ className } as Record<string, string>) : {};

/** Prevent customStyling minHeight:0 / flex crush on web — labels need intrinsic height. */
const mergeWebFormRootStyle = (base: ViewStyle, custom?: object): ViewStyle => {
  if (Platform.OS !== 'web') {
    return StyleSheet.flatten([base, custom]) as ViewStyle;
  }
  const customFlat = custom ? (StyleSheet.flatten(custom) as ViewStyle) : {};
  const { minHeight: _omitMinHeight, ...customRest } = customFlat;
  return StyleSheet.flatten([
    base,
    customRest,
    {
      flexShrink: 0,
      alignSelf: 'stretch',
      width: '100%',
    },
  ]) as ViewStyle;
};
import { Formik, FormikProps, Field, setNestedObjectValues, FormikTouched, FormikHelpers, useField, FastField, FieldProps, useFormikContext } from "formik";
import { Text, View } from "@constants/Themed";
import React, { JSX, forwardRef, memo, RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styles from "@stylesheet";
import { ScrollView } from "@constants/Themed";
import useGlobalUser from "./getGlobalUser";
// import Button from "@components/UI/Button/Button";
import { Spinner, XStack } from 'tamagui'
import AuthModal from "@components/modals/ErrorModal/AuthModal";
import { FormValues } from "@components/Global/types";
import parseByDelimiter from "@utils/parseByDelimiter";
import generateYupValidation from "./generateYupValidation";
import * as Animatable from 'react-native-animatable';
import Row from './rowComp';
import { MotiPressable } from 'moti/interactions';
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { LinearProgress } from "@constants/Themed";
import { responsiveFontSize } from "@utils/responsiveFontSize";
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { evaluateFormCondition } from '@utils/evaluateFormCondition';
import { FormPickerCoordinatorProvider } from '@context/FormPickerCoordinatorContext';
import type { SchemaItem } from '../types/handleFieldType';
import {
  isSchemaKeyNullified,
  normalizeSchemaEntries,
} from './schemaForm/normalizeSchemaEntries';

const isComputedLookupField = (field: any) =>
  ['keywordlookup', 'keyword_lookup'].includes(
    String(field?.type || field?.ref || '').toLowerCase()
  );

const omitComputedLookupValues = (values: Record<string, any>, schemaFields: any[] = []) => {
  const computedKeys = new Set(
    schemaFields
      .filter(isComputedLookupField)
      .map((field) => field?.key || field?.title)
      .filter(Boolean)
  );
  if (!computedKeys.size) return values;
  const next = { ...values };
  for (const key of computedKeys) {
    delete next[key];
  }
  return next;
};

const FormikDebugger = ({ status }) => {
  useEffect(() => {
    console.log("Formik State Changed:", {
      status
    });
  }, [status]);

  return null; // no visible UI
};

export interface SchemaObject {
  /**
   * @param schema - The schema object to be generated
   */
  schema: Record<string, SchemaItem> | SchemaItem[],
  /**
   * @param staged - Whether the form is staged, introduce one field at a time
   */
  staged? : boolean,
  /**
   * @param submitFunction - The function to be called when the form is submitted
   */
  submitFunction?: Function,
  /**
   * @param nullify - The fields to be nullified
   */
  nullify?: Array<string>,
  /**
   * @param sheetRef - The reference to the bottom sheet
   */
  sheetRef?: RefObject<FormikHelpers<any>>,
  /**
   * @param other - The id of the data to be submitted, this should be the _id of the document
   */
  other?: any,
  /**
   * @param otherVals - The other values to be passed into special fields
   */
  otherVals?: Record<string, any>;
  /**
   * @param callback - The callback function to be called after submission
   * @requires state - The state to be updated after submission
   * @requires setState - The function to update the state
   */
  callback?: { state: boolean, setState: Function },
  /**
   * @param dispatch - The dispatch function to be called after submission
   */
  dispatch?: (_id: string)=> void,
  /**
   * @param internalButton - Whether the button is internal
   */
  internalButton?: boolean,
  /**
   * @param buttonLabel - The label of the button
   */
  buttonLabel?: string, // Make buttonLabel optional as it can be provided by renderFormControls
  buttonIcon?: string | JSX.Element,
  /**
   * @param buttonStyle - The style of the button
   */
  buttonStyle?: any,
  /**
   * @param avoidKeyboardDisabled - Whether the keyboard should be avoided
   * @description This is only applicable for mobile devices
   * @default false
   */
  avoidKeyboardDisabled?: boolean,
  /**
   * @param Progress - Whether to display the ProgressBar bar
   * @default false
   */
  Progress?: boolean,
  /**
   * @param noBackground - Whether to display the background
   * @default false
   */
  noBackground?: boolean,
  /**
   * @param retainInitialValues - Should passed values be kept after submission
   * @default false
   */
  retainInitialValues?: boolean,
  /**
   * @param initialData - The initial data passed to the form in the form of an object
   */
  initialData?: any,
  addOffset?: number,
  /**
   * @param hasTitle - Whether the schema has a title
   * @default false
   */
  hasTitle?: boolean,
  /**
   * @param columns - The number of columns to be displayed
   */
  columns?: number,
  /**
   * @param extraFunction - The extra function to be called after submission
   * @default null
   * */
  extraFunction?: Function,
  customStyling?: Object,
  addPadding?: boolean,
  textColor?: string,
  extraCallBack?: Function,
  /**
   * When true, `extraCallBack` runs right after a successful submit instead of after the 3s delay.
   * @default false
   */
  extraCallBackImmediate?: boolean,
  schemaName?: string,
  extraId?: string,
  buttonColor?: string,
  /**
   * @param scrollEnabled - Whether the form should be scrollable
   * @default true
   */
  scrollEnabled?: boolean,
  /**
   * Native: use with parent BottomSheetScrollView + scrollEnabled={false}.
   * Drops flex:1 / minHeight so content height follows fields instead of stretching past the screen.
   */
  embeddedInBottomSheetScroll?: boolean,
  /**
   * @param scrollBarVisible - Whether the scrollbar should be visible
   * @default true
   */
  scrollBarVisible?: boolean,
  hideButton?: boolean,
  removeSubmit?: boolean,
  bottomPadding?: number,
  containerWidth?: number,
  hideLabel?: boolean,
  /**
   * @param extraValues - The extra values to be passed to the submit function
   * @default {}
   */
  extraValues?: Object,
  blackText?: boolean,
  /**
   * @param resetOnSubmit - Whether to reset the form on submit
   * @default false
   */
  resetOnSubmit?: boolean,
  /**
   * @param endReached - Callback triggered when reaching the last question in staged mode
   * @param values - The current form values
   * @default undefined
   */
  endReached?: (values: any) => void,
  /**
   * @param showSkipButton - Whether to show a skip button
   * @default false
   */
  showSkipButton?: boolean,
  /**
   * @param skipButtonLabel - The label for the skip button
   * @default "Skip"
   */
  skipButtonLabel?: string,
  /**
   * @param onSkip - Callback function called when skip button is pressed
   * @default undefined
   */
  onSkip?: () => void,
  /**
   * @param readOnly - Whether the form should be in read-only mode (fields disabled/read-only)
   * @default false
   */
  readOnly?: boolean,
  /**
   * @param renderFormControls - A render function to provide custom form controls (e.g., submit button).
   * Takes FormikProps as an argument. If provided, the internal button will not be rendered.
   */
  renderFormControls?: (formikProps: FormikProps<any>) => React.ReactNode,
  /**
   * @param onCancel - Callback function called when cancel button is pressed
   * @default undefined
   */
  onCancel?: () => void,
  /**
   * @param cancelButtonLabel - The label for the cancel button
   * @default "Cancel"
   */
  cancelButtonLabel?: string,
  /**
   * @param inlineFields - Whether to render fields in an XStack with flexWrap="wrap"
   * @default false
   */
  inlineFields?: boolean,
  /**
   * @param hasBack - Whether to show a back button
   * @default false
   */
  hasBack?: boolean,
  /**
   * @param backButton - Custom back button element, or string label for default back button
   * @default "Back"
   */
  backButton?: string | React.ReactNode,
  /**
   * @param backButtonFn - Callback when back button is pressed
   */
  backButtonFn?: () => void,
  /**
   * @param fieldStyle - Custom style for form fields
   */
  fieldStyle?: any,
  /**
   * @param labelStyle - Custom style for field labels
   */
  labelStyle?: any,
  /**
   * @param formStyle - Custom style for the form container
   */
  formStyle?: any,
  /**
   * Called when form values change. Used by callers that preview a selection beside the form.
   */
  onValuesChange?: (values: Record<string, unknown>) => void,
}

// FormBody component - extracted from Formik render callback to fix Rules of Hooks violation
interface FormBodyProps {
  formikProps: {
    validateForm: any;
    resetForm: any;
    handleChange: any;
    setFormikState: any;
    isSubmitting: boolean;
    touched: any;
    setSubmitting: (value: boolean) => void;
    handleSubmit: any;
    submitForm: any;
    errors: any;
    values: any;
    status: any;
  };
  props: SchemaObject;
  schemaArray: any[];
  formInstanceId: string;
  currentQuestionIndex: number;
  setCurrentQuestionIndex: (index: number) => void;
  endReachedTriggered: React.MutableRefObject<boolean>;
  handleFocusNextField: any;
  setInputRef: any;
  getVisibleFields: (values: any) => any[];
  RenderStagedRows: any;
  RenderRows: any;
  isDevice: boolean;
  isTouchDeviceRef: React.MutableRefObject<boolean>;
  shouldPreventBlurRef: React.MutableRefObject<boolean>;
  focusTimeRef: React.MutableRefObject<number>;
  columns: number;
  inlineFields?: boolean;
  fieldStyle?: any;
  labelStyle?: any;
}

const FormBody = React.memo(({
  formikProps,
  props,
  schemaArray,
  formInstanceId,
  currentQuestionIndex,
  setCurrentQuestionIndex,
  endReachedTriggered,
  handleFocusNextField,
  setInputRef,
  getVisibleFields,
  RenderStagedRows,
  RenderRows,
  isDevice,
  isTouchDeviceRef,
  shouldPreventBlurRef,
  focusTimeRef,
  columns,
  inlineFields,
  fieldStyle,
  labelStyle
}: FormBodyProps) => {
  // Get theme - this is now in a proper component, so hooks are allowed
  const theme = themeSelector();

  // Subscribe to Formik context so conditional visibility updates when ANY field changes.
  // (FormBody receives formikProps from render prop, but React.memo + FastField's selective
  // updates can prevent re-renders; context subscription guarantees we see value changes.)
  const { values: contextValues } = useFormikContext<any>();
  const values = contextValues ?? formikProps.values;

  const { validateForm, setFormikState, isSubmitting, setSubmitting, handleSubmit, errors, status } = formikProps;
  const onValuesChange = props.onValuesChange;
  const lastReportedValues = useRef('');

  useEffect(() => {
    if (!onValuesChange) return;
    const serialized = JSON.stringify(values ?? {});
    if (serialized === lastReportedValues.current) return;
    lastReportedValues.current = serialized;
    onValuesChange(values ?? {});
  }, [onValuesChange, values]);

  // For MUI LinearProgress, value should be between 0 and 100
  const handleUpdateProgressBar = () => {
    const visibleFields = getVisibleFields(values || {});
    if (!visibleFields?.length) return 0;
    const singleValue = 100 / visibleFields.length;
    let progressValue = 0;
    if (!values) return 0;

    // Only count progress for visible fields
    visibleFields.forEach(field => {
      const fieldKey = field.title || field.key;
      const value = values[fieldKey];
      if (value !== 'null' && !isSchemaKeyNullified(fieldKey, props.nullify)) {
        if (Array.isArray(value)) {
          if (value?.length >= 1) {
            progressValue += singleValue;
          }
        } else if (value != null && value !== '' && value) {
          progressValue += singleValue;
        }
      }
    });
    // Clamp between 0 and 100
    return Math.min(progressValue, 100);
  };

  // Recompute whenever Formik values change so dependsOn / conditional field visibility stays in sync.
  // (A prior dependency shortcut only tracked array length for multi-selects, which could skip updates.)
  const memoizedVisibleFields = useMemo(() => {
    return getVisibleFields(values || {});
  }, [values, getVisibleFields]);

  // Trigger endReached callback when on the last question in staged mode
  useEffect(() => {
    if (props.staged && props.endReached && memoizedVisibleFields && memoizedVisibleFields.length > 0) {
      if (currentQuestionIndex === memoizedVisibleFields.length - 1 && !endReachedTriggered.current) {
        // We're on the last question and haven't triggered the callback yet
        endReachedTriggered.current = true;
        props.endReached(values || {});
      }
    }
  }, [currentQuestionIndex, memoizedVisibleFields?.length, props.staged, props.endReached, values]);

  // Don't wrap entire JSX in useMemo - just return it directly
  const { t } = useTranslation();

  /** Native: submit row — must scroll with fields inside bottom sheets (otherwise it sits below the fold / clips). */
  const renderNativeSubmitButtons = () => {
    const shouldShowButton = !props.internalButton && !props.removeSubmit && !props.hideButton;

    if (!shouldShowButton) {
      return null;
    }

    return (
      <View style={[
        {
          width: '100%',
          maxWidth: '100%',
          alignItems: 'stretch',
          justifyContent: 'center',
          paddingHorizontal: 16,
          paddingVertical: 16,
          backgroundColor: 'transparent',
          flexDirection: (props.showSkipButton || props.onCancel) ? 'row' : 'column',
          gap: (props.showSkipButton || props.onCancel) ? 12 : 0,
          flexWrap: 'nowrap',
        },
        props.buttonStyle,
        { backgroundColor: 'transparent' }
      ]}>
        {props.showSkipButton && (
          <Pressable
            onPress={() => {
              console.log('[GenerateSchemaForm] Skip button pressed');
              if (props.onSkip) {
                props.onSkip();
              }
            }}
            disabled={isSubmitting}
            style={{
              minHeight: 48,
              minWidth: 120,
              maxWidth: '100%',
              paddingVertical: 14,
              paddingHorizontal: 24,
              borderRadius: 12,
              backgroundColor: 'transparent',
              borderWidth: 1.5,
              borderColor: theme ? '#6b7280' : '#9ca3af',
              justifyContent: 'center',
              alignItems: 'center',
              opacity: isSubmitting ? 0.6 : 1,
            }}
          >
            <Text style={{
              color: theme ? '#6b7280' : '#9ca3af',
              fontSize: responsiveFontSize(14),
              fontWeight: '600',
            }}>
              {props.skipButtonLabel || 'Skip'}
            </Text>
          </Pressable>
        )}
        {props.onCancel && (
          <Button
            disabled={isSubmitting}
            isLoading={false}
            isSuccess={false}
            icon={undefined}
            onPress={() => {
              console.log('[GenerateSchemaForm] Cancel button pressed');
              if (props.onCancel) {
                props.onCancel();
              }
            }}
            buttonText={props.cancelButtonLabel || 'Cancel'}
            customStyle={{
              minHeight: 48,
              minWidth: 0,
              maxWidth: '100%',
              width: '100%',
              flex: 1,
              flexShrink: 1,
            }}
            defaultColor="#6b7280"
          />
        )}
        <Button disabled={props.removeSubmit}
          icon={props.buttonIcon}
          isSuccess={status?.success === true}
          isLoading={isSubmitting}
          customStyle={{
            minHeight: 48,
            minWidth: 0,
            maxWidth: '100%',
            width: (props.showSkipButton || props.onCancel) ? '100%' : '100%',
            flex: (props.showSkipButton || props.onCancel) ? 1 : undefined,
            flexShrink: 1,
            alignSelf: (props.showSkipButton || props.onCancel) ? undefined : 'center',
          }}
          defaultColor={props.buttonColor ? (Object.keys(errors)?.length != 0 ? '#9ca3af' : props.buttonColor) : '#3b82f6'}
          onPress={() => {
            setSubmitting(true);
            validateForm().then(res => {
              const visibleFields = getVisibleFields(values || {});
              const visibleFieldNames = new Set(visibleFields.map(f => f.title || f.key));
              const filteredErrors: any = {};
              if (res) {
                Object.keys(res).forEach(fieldName => {
                  if (visibleFieldNames.has(fieldName)) {
                    filteredErrors[fieldName] = res[fieldName];
                  } else {
                    console.log(`⏭️  Skipping validation error for hidden field: ${fieldName}`);
                  }
                });
              }

              if (filteredErrors && Object.keys(filteredErrors)?.length != 0) {
                setFormikState(prevState => ({ ...prevState, touched: setNestedObjectValues<FormikTouched<FormValues>>(filteredErrors, true) }));
                setTimeout(() => {
                  setFormikState(prevState => ({ ...prevState, status: null }));
                }, 500);
                setSubmitting(false);
              } else {

                const visibleFieldsInner = getVisibleFields(values || {});
                const visibleFieldNamesInner = new Set(visibleFieldsInner.map(f => f.title || f.key));

                const cleanedErrors: any = {};
                if (errors) {
                  Object.keys(errors).forEach(fieldName => {
                    if (visibleFieldNamesInner.has(fieldName)) {
                      cleanedErrors[fieldName] = errors[fieldName];
                    }
                  });
                }

                console.log("🧹 [GenerateSchemaForm] Cleaning Formik errors - removing hidden field errors");
                console.log("📊 Errors before cleaning:", Object.keys(errors || {}));
                console.log("📊 Errors after cleaning:", Object.keys(cleanedErrors));

                setFormikState((prevState: any) => ({
                  ...prevState,
                  errors: cleanedErrors
                }));

                setSubmitting(true);

                try {
                  console.log("🚀 [GenerateSchemaForm] Triggering handleSubmit()...");
                  handleSubmit();
                  console.log("✅ [GenerateSchemaForm] handleSubmit() called");
                } catch (error) {
                  console.error("❌ [GenerateSchemaForm] Error calling handleSubmit():", error);
                  setSubmitting(false);
                }
              }
            }).catch(error => {
              console.error("❌ [GenerateSchemaForm] Validation error:", error);
              setSubmitting(false);
            });
          }} buttonText={props.buttonLabel || 'Submit'} />
      </View>
    );
  };

  const delegateScrollToParent = Platform.OS !== 'web' && props.scrollEnabled === false;

  return (
    <FormPickerCoordinatorProvider>
    <View
      style={{
        flex: delegateScrollToParent ? 0 : 1,
        minHeight: Platform.OS === 'web' ? undefined : 200,
        width: Platform.OS === 'web' ? '100%' : undefined,
        alignSelf: Platform.OS === 'web' ? 'stretch' : undefined,
        backgroundColor: 'transparent',
        gap: 20,
      }}
      pointerEvents="box-none"
      // Prevent touch events from bubbling to document (which might cause focus loss)
      onTouchStart={(e) => {
        if (Platform.OS === 'web' && isTouchDeviceRef.current) {
          e.stopPropagation();
          shouldPreventBlurRef.current = true;
          // Reset after a short delay
          setTimeout(() => {
            shouldPreventBlurRef.current = false;
          }, 100);
        }
      }}
      onTouchEnd={(e) => {
        if (Platform.OS === 'web' && isTouchDeviceRef.current) {
          e.stopPropagation();
        }
      }}
      onTouchMove={(e) => {
        if (Platform.OS === 'web' && isTouchDeviceRef.current) {
          e.stopPropagation();
        }
      }}
    >
      {props.hasBack && props.backButtonFn && (
        <View style={{ marginBottom: 16, alignSelf: 'flex-start' }}>
          {typeof props.backButton === 'object' && props.backButton != null ? (
            <Pressable onPress={props.backButtonFn}>
              {props.backButton}
            </Pressable>
          ) : (
            <Pressable
              onPress={props.backButtonFn}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                paddingVertical: 10,
                paddingHorizontal: 16,
                borderRadius: 8,
                backgroundColor: theme ? 'rgba(107, 114, 128, 0.1)' : 'rgba(107, 114, 128, 0.2)',
                borderWidth: 1,
                borderColor: theme ? '#6b7280' : '#9ca3af',
              }}
            >
              <Text style={{ color: theme ? '#6b7280' : '#9ca3af', fontSize: responsiveFontSize(14), fontWeight: '600' }}>
                ← {typeof props.backButton === 'string' ? props.backButton : 'Back'}
              </Text>
            </Pressable>
          )}
        </View>
      )}
      {props.Progress && Platform.OS === 'web' &&
        <View style={{ flex: 1, maxHeight: 15, alignSelf: "center", flexDirection: 'row', maxWidth: '70%', justifyContent: 'center', gap: 20, marginVertical: 10 }}>
          <View style={{ minHeight: 10, maxWidth: '100%' }}>
            {LinearProgress && (
              <LinearProgress variant="determinate" value={handleUpdateProgressBar()} />
            )}
          </View>
        </View>
      }
      {Platform.OS === 'web' ? (
        <View style={{
          flex: props.scrollEnabled ? 1 : 0,
          flexShrink: 0,
          gap: 30,
          minWidth: props.staged ? 400 : undefined,
          width: '100%',
          alignSelf: 'stretch',
          minHeight: 0,
          backgroundColor: 'transparent',
          zIndex: 1
        }}>
          {
            props.scrollEnabled ?
              <ScrollView
                style={{ flex: 1, minHeight: 0, width: '100%' }}
                scrollEnabled={props.scrollEnabled || true}
                showsVerticalScrollIndicator={props.scrollBarVisible !== undefined ? props.scrollBarVisible : true}
                nestedScrollEnabled
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode={Platform.OS === 'web' ? "none" : "on-drag"}
                contentContainerStyle={{
                  backgroundColor: 'transparent',
                  gap: 20,
                  paddingTop: 10,
                  paddingBottom: 16,
                  alignItems: 'stretch',
                  justifyContent: 'flex-start',
                  flexGrow: 0,
                  flexShrink: 0,
                  width: '100%',
                  zIndex: 1,
                }}
                {...webFormClass('ss-schema-form-scroll-content')}
              >
                <View {...webFormClass('ss-schema-form-fields')} style={{ width: '100%' }}>
                {props.staged ? (
                  <RenderStagedRows
                    otherVals={props.otherVals}
                    hideLabel={false}
                    blackText={undefined}
                    visibleFields={memoizedVisibleFields}
                    currentIndex={currentQuestionIndex}
                    nextFieldFunction={handleFocusNextField}
                    setInputRef={setInputRef}
                    values={values}
                    validateForm={validateForm}
                    readOnly={props.readOnly}
                    fieldStyle={fieldStyle}
                    labelStyle={labelStyle}
                  />
                ) : (
                    <RenderRows hideLabel={props.hideLabel} blackText={undefined} otherVals={props.otherVals} nextFieldFunction={handleFocusNextField} setInputRef={setInputRef} schemaArray={memoizedVisibleFields} columns={columns} formInstanceId={formInstanceId} readOnly={props.readOnly} inlineFields={props.inlineFields} insideBottomSheet={props.embeddedInBottomSheetScroll} fieldStyle={fieldStyle} labelStyle={labelStyle} />
                )}
                </View>
              </ScrollView>
              :
              <View
                {...webFormClass('ss-schema-form-fields')}
                style={{
                minHeight: Platform.OS === 'web' ? 'auto' : 200,
                width: '100%',
                flexShrink: 0,
                alignSelf: 'stretch',
                overflow: 'visible',
                position: Platform.OS === 'web' ? 'relative' : undefined,
                zIndex: Platform.OS === 'web' ? 1 : undefined
              }}>
                {props.staged ? (
                  <RenderStagedRows
                    otherVals={props.otherVals}
                    hideLabel={props.hideLabel}
                    blackText={undefined}
                    visibleFields={memoizedVisibleFields}
                    currentIndex={currentQuestionIndex}
                    nextFieldFunction={handleFocusNextField}
                    setInputRef={setInputRef}
                    values={values}
                    validateForm={validateForm}
                    readOnly={props.readOnly}
                    fieldStyle={fieldStyle}
                    labelStyle={labelStyle}
                  />
                ) : (
                    <RenderRows hideLabel={props.hideLabel} blackText={undefined} otherVals={props.otherVals} nextFieldFunction={handleFocusNextField} setInputRef={setInputRef} schemaArray={memoizedVisibleFields} columns={columns} formInstanceId={formInstanceId} readOnly={props.readOnly} inlineFields={props.inlineFields} insideBottomSheet={props.embeddedInBottomSheetScroll} fieldStyle={fieldStyle} labelStyle={labelStyle} />
                )}
              </View>
          }
          {(() => {
            const shouldShowButton = !props.internalButton && !props.removeSubmit && !props.hideButton;

            if (!shouldShowButton) {
              return null;
            }

            return (
              <View style={[
                {
                  width: '100%',
                  maxWidth: '100%',
                  alignItems: 'stretch',
                  justifyContent: 'center',
                  paddingHorizontal: 16,
                  paddingVertical: 16,
                  backgroundColor: 'transparent',
                  flexDirection: (props.showSkipButton || props.onCancel) ? 'row' : 'column',
                  gap: (props.showSkipButton || props.onCancel) ? 12 : 0,
                  flexWrap: 'nowrap',
                },
                props.buttonStyle,
                { backgroundColor: 'transparent' }
              ]}>
                {props.showSkipButton && (
                  <Pressable
                    onPress={() => {
                      console.log('[GenerateSchemaForm] Skip button pressed');
                      if (props.onSkip) {
                        props.onSkip();
                      }
                    }}
                    disabled={isSubmitting}
                    style={{
                      minHeight: 48,
                      minWidth: 120,
                      maxWidth: Platform.OS === 'web' ? 200 : '100%',
                      paddingVertical: 14,
                      paddingHorizontal: 24,
                      borderRadius: 12,
                      backgroundColor: 'transparent',
                      borderWidth: 1.5,
                      borderColor: theme ? '#6b7280' : '#9ca3af',
                      justifyContent: 'center',
                      alignItems: 'center',
                      opacity: isSubmitting ? 0.6 : 1,
                    }}
                  >
                    <Text style={{
                      color: theme ? '#6b7280' : '#9ca3af',
                      fontSize: responsiveFontSize(14),
                      fontWeight: '600',
                    }}>
                      {props.skipButtonLabel || 'Skip'}
                    </Text>
                  </Pressable>
                )}
                {props.onCancel && (
                  <Button
                    disabled={isSubmitting}
                    isLoading={false}
                    isSuccess={false}
                    icon={undefined}
                    onPress={() => {
                      console.log('[GenerateSchemaForm] Cancel button pressed');
                      if (props.onCancel) {
                        props.onCancel();
                      }
                    }}
                    buttonText={props.cancelButtonLabel || 'Cancel'}
                    customStyle={{
                      minHeight: 48,
                      minWidth: 0,
                      maxWidth: '100%',
                      width: '100%',
                      flex: 1,
                      flexShrink: 1,
                    }}
                    defaultColor="#6b7280"
                  />
                )}
                <Button disabled={props.removeSubmit}
                  icon={props.buttonIcon}
                  isSuccess={status?.success === true}
                  isLoading={isSubmitting}
                  customStyle={{
                    minHeight: 48,
                    minWidth: 0,
                    maxWidth: '100%',
                    width: (props.showSkipButton || props.onCancel) ? '100%' : (Platform.OS === 'web' ? 'auto' : '100%'),
                    flex: (props.showSkipButton || props.onCancel) ? 1 : undefined,
                    flexShrink: 1,
                    alignSelf: (props.showSkipButton || props.onCancel) ? undefined : 'center',
                  }}
                  defaultColor={props.buttonColor ? (Object.keys(errors)?.length != 0 ? '#9ca3af' : props.buttonColor) : '#3b82f6'}
                  onPress={() => {
                    setSubmitting(true);
                    validateForm().then(res => {
                      // CRITICAL: Filter out validation errors for hidden/conditional fields
                      const visibleFields = getVisibleFields(values || {});
                      const visibleFieldNames = new Set(visibleFields.map(f => f.title || f.key));
                      const filteredErrors: any = {};
                      if (res) {
                        Object.keys(res).forEach(fieldName => {
                          if (visibleFieldNames.has(fieldName)) {
                            // Only include errors for visible fields
                            filteredErrors[fieldName] = res[fieldName];
                          } else {
                            console.log(`⏭️  Skipping validation error for hidden field: ${fieldName}`);
                          }
                        });
                      }

                      if (filteredErrors && Object.keys(filteredErrors)?.length != 0) {
                        setFormikState(prevState => ({ ...prevState, touched: setNestedObjectValues<FormikTouched<FormValues>>(filteredErrors, true) }));
                        setTimeout(() => {
                          setFormikState(prevState => ({ ...prevState, status: null }));
                        }, 500);
                        setSubmitting(false);
                      } else {

                        // CRITICAL: Clear validation errors for hidden fields in Formik state
                        // This prevents Formik's handleSubmit from seeing errors for hidden fields
                        const visibleFields = getVisibleFields(values || {});
                        const visibleFieldNames = new Set(visibleFields.map(f => f.title || f.key));

                        // Create a cleaned errors object with only visible field errors
                        const cleanedErrors: any = {};
                        if (errors) {
                          Object.keys(errors).forEach(fieldName => {
                            if (visibleFieldNames.has(fieldName)) {
                              cleanedErrors[fieldName] = errors[fieldName];
                            }
                          });
                        }

                        console.log("🧹 [GenerateSchemaForm] Cleaning Formik errors - removing hidden field errors");
                        console.log("📊 Errors before cleaning:", Object.keys(errors || {}));
                        console.log("📊 Errors after cleaning:", Object.keys(cleanedErrors));

                        // Update Formik's errors state to exclude hidden field errors - only call setFormikState once
                        // This ensures handleSubmit() won't see errors for hidden fields
                        setFormikState((prevState: any) => ({
                          ...prevState,
                          errors: cleanedErrors
                        }));

                        setSubmitting(true);

                        // Now call handleSubmit - it will validate, but we've already cleared the errors
                        // So the validation should pass (or at least only show visible field errors)
                        try {
                          console.log("🚀 [GenerateSchemaForm] Triggering handleSubmit()...");
                          handleSubmit();
                          console.log("✅ [GenerateSchemaForm] handleSubmit() called");
                        } catch (error) {
                          console.error("❌ [GenerateSchemaForm] Error calling handleSubmit():", error);
                          setSubmitting(false);
                        }
                      }
                    }).catch(error => {
                      console.error("❌ [GenerateSchemaForm] Validation error:", error);
                      setSubmitting(false);
                    });
                  }} buttonText={props.buttonLabel || 'Submit'} />
              </View>
            );
          })()}
        </View>
      ) : (
        <KeyboardAvoidingView style={{
          flex: props.embeddedInBottomSheetScroll || delegateScrollToParent ? 0 : 1,
          gap: 30,
          backgroundColor: 'transparent',
          zIndex: 1
        }}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          enabled={isDevice && !props.avoidKeyboardDisabled && !props.embeddedInBottomSheetScroll}>
          {
            props.scrollEnabled ?
              <ScrollView
                style={{ flex: 1, minHeight: 0 }}
                scrollEnabled={props.scrollEnabled || true}
                showsVerticalScrollIndicator={props.scrollBarVisible !== undefined ? props.scrollBarVisible : true}
                nestedScrollEnabled
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode="on-drag"
                contentContainerStyle={{
                  backgroundColor: 'transparent',
                  gap: 20,
                  paddingTop: 10,
                  paddingBottom: 28,
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  zIndex: 1
                }}
              >
                {props.staged ? (
                  <RenderStagedRows
                    otherVals={props.otherVals}
                    hideLabel={false}
                    blackText={undefined}
                    visibleFields={memoizedVisibleFields}
                    currentIndex={currentQuestionIndex}
                    nextFieldFunction={handleFocusNextField}
                    setInputRef={setInputRef}
                    values={values}
                    validateForm={validateForm}
                    readOnly={props.readOnly}
                    fieldStyle={fieldStyle}
                    labelStyle={labelStyle}
                  />
                ) : (
                      <RenderRows hideLabel={props.hideLabel} blackText={undefined} otherVals={props.otherVals} nextFieldFunction={handleFocusNextField} setInputRef={setInputRef} schemaArray={memoizedVisibleFields} columns={columns} formInstanceId={formInstanceId} readOnly={props.readOnly} inlineFields={props.inlineFields} insideBottomSheet={props.embeddedInBottomSheetScroll} fieldStyle={fieldStyle} labelStyle={labelStyle} />
                )}
                {renderNativeSubmitButtons()}
              </ScrollView>
              :
              <View style={{
                minHeight: 200,
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                alignSelf: 'stretch',
                overflow: 'hidden',
                zIndex: 1
              }}>
                {props.staged ? (
                  <RenderStagedRows
                    otherVals={props.otherVals}
                    hideLabel={props.hideLabel}
                    blackText={undefined}
                    visibleFields={memoizedVisibleFields}
                    currentIndex={currentQuestionIndex}
                    nextFieldFunction={handleFocusNextField}
                    setInputRef={setInputRef}
                    values={values}
                    validateForm={validateForm}
                    readOnly={props.readOnly}
                    fieldStyle={fieldStyle}
                    labelStyle={labelStyle}
                  />
                ) : (
                      <RenderRows hideLabel={props.hideLabel} blackText={undefined} otherVals={props.otherVals} nextFieldFunction={handleFocusNextField} setInputRef={setInputRef} schemaArray={memoizedVisibleFields} columns={columns} formInstanceId={formInstanceId} readOnly={props.readOnly} inlineFields={props.inlineFields} insideBottomSheet={props.embeddedInBottomSheetScroll} fieldStyle={fieldStyle} labelStyle={labelStyle} />
                )}
              </View>
          }
          {!props.scrollEnabled && renderNativeSubmitButtons()}
        </KeyboardAvoidingView>
      )}
    </View>
    </FormPickerCoordinatorProvider>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  // Only re-render if significant props change, not on every Formik state update (like values/touched)

  // Always re-render if these change (they affect structure)
  if (
    prevProps.props.schema !== nextProps.props.schema ||
    prevProps.props.staged !== nextProps.props.staged ||
    prevProps.props.scrollEnabled !== nextProps.props.scrollEnabled ||
    prevProps.props.embeddedInBottomSheetScroll !== nextProps.props.embeddedInBottomSheetScroll ||
    prevProps.schemaArray.length !== nextProps.schemaArray.length ||
    prevProps.formInstanceId !== nextProps.formInstanceId ||
    prevProps.currentQuestionIndex !== nextProps.currentQuestionIndex ||
    prevProps.isDevice !== nextProps.isDevice ||
    prevProps.columns !== nextProps.columns
  ) {
    return false; // Props changed, allow re-render
  }

  // Check formikProps that matter for rendering
  // Don't compare values/touched as they change on every keystroke
  const prevErrors = prevProps.formikProps.errors || {};
  const nextErrors = nextProps.formikProps.errors || {};
  const errorsChanged = JSON.stringify(Object.keys(prevErrors).sort()) !==
    JSON.stringify(Object.keys(nextErrors).sort());

  if (
    prevProps.formikProps.isSubmitting !== nextProps.formikProps.isSubmitting ||
    prevProps.formikProps.status !== nextProps.formikProps.status ||
    errorsChanged
  ) {
    return false; // Significant formik state changed, allow re-render
  }

  // No significant changes, prevent re-render
  return true;
});

const GenerateSchemaForm = forwardRef<FormikProps<any>, SchemaObject>((props, ref) => {
  useEffect(() => {
    if (Platform.OS === 'web') loadSchemaFormStyles();
  }, []);
  const [schemaArray, setSchemaArray] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState(props.initialData || {});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const id = useGlobalUser();
  const combinedIds = useMemo(() => ({ globalId: id, dataId: props.other, extraId: props.extraId || null }), [id, props.other, props.extraId]);
  const inputRefs = useRef({});
  const isDevice = Platform.OS !== 'web';
  const [columns, setColumns] = useState(1);
  const endReachedTriggered = useRef(false); // Track if endReached has been triggered for current question
  // Generate a unique form instance ID to prevent duplicate input IDs
  const formInstanceId = useRef(`form-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`).current;
  // Detect touch device for web to prevent keyboard dismissal
  const isTouchDeviceRef = useRef<boolean>(false);
  // Track if we should prevent blur after touch events
  const shouldPreventBlurRef = useRef<boolean>(false);
  // Track focus time to prevent immediate blur after focus
  const focusTimeRef = useRef<number>(0);
  // Track if schema has been processed to prevent re-processing on re-renders
  const schemaProcessedRef = useRef<boolean>(false);
  const processedSchemaRef = useRef<string | null>(null);
  // Track last applied initialData (by content) so we only sync when it actually changes
  const lastInitialDataSerializedRef = useRef<string | null>(null);
  // Track window dimensions for keyboard detection
  const windowDimensionsTrackingRef = useRef<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      isTouchDeviceRef.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      // Initialize dimensions
      windowDimensionsTrackingRef.current = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      // REMOVED: Window resize listener - it was causing parent LoginScreen to re-render
      // The resize event bubbles up and triggers useWindowDimensions() in LoginScreen
      // Even though we weren't setting state, the window resize itself causes the hook to update
      // This was causing LoginScreen to re-render when keyboard appeared in the form

      // REMOVED: Visual viewport listener - it was causing re-renders on keyboard appearance
      // The viewport changes were triggering component re-renders even though we weren't setting state
      // This was interrupting form focus on touch devices
    }
  }, []);
  // const [reset, setReset] = useState(false);
  // const [submitDisabled, setSubmitDisabled] = useState(false);

  const propChangeCount = useRef(0);

  const evaluateCondition = useCallback((fieldSchema: any, formValues: any, allSchemaFields: any[] = []): boolean => {
    return evaluateFormCondition(fieldSchema, formValues, allSchemaFields);
  }, []);

  // Get visible fields based on current values and conditions
  const getVisibleFields = useCallback((formValues: any) => {
    const results = schemaArray.map(field => {
      const isVisible = evaluateCondition(field, formValues, schemaArray);
      return {
        field: field.title || field.key,
        isVisible,
        hasCondition: !!(field.conditional || field.dependsOn || field.validation?.dependsOn)
      };
    });
    
    
    return schemaArray.filter(field => evaluateCondition(field, formValues, schemaArray));
  }, [schemaArray, evaluateCondition]);

  // Navigation functions for staged form
  const goToNextQuestion = useCallback((visibleFields: any[], formValues: any, validateForm: Function) => {
    if (currentQuestionIndex < visibleFields.length - 1) {
      const currentField = visibleFields[currentQuestionIndex];

      // Validate current field before proceeding
      if (currentField?.validation && currentField.validation.required) {
        const fieldValue = formValues[currentField.key];
        if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
          return false; // Validation failed, don't proceed
        }
      }

      setCurrentQuestionIndex(prev => {
        const nextIndex = prev + 1;
        // Reset the endReached flag when moving to a new question
        endReachedTriggered.current = false;

        // Check if we're reaching the last question
        if (nextIndex === visibleFields.length - 1 && props.endReached) {
          // Trigger endReached callback when reaching the last question
          setTimeout(() => {
            if (!endReachedTriggered.current) {
              endReachedTriggered.current = true;
              props.endReached?.(formValues);
            }
          }, 100);
        }
        return nextIndex;
      });
      return true;
    }
    return false;
  }, [currentQuestionIndex, props.endReached]);

  const goToPreviousQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      // Reset the endReached flag when moving to a different question
      endReachedTriggered.current = false;
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex]);

  // Effect to trigger endReached callback when reaching the last question
  useEffect(() => {
    if (props.staged && props.endReached && schemaArray.length > 0) {
      // This effect will be triggered by currentQuestionIndex changes
      // The actual callback with values will be handled in the Formik render function
    }
  }, [currentQuestionIndex, schemaArray.length, props.staged, props.endReached]);

  // When props.initialData content changes (e.g. parent refetched user), sync initialValues
  // so the form displays the latest data. Only update when serialized content actually changed.
  useEffect(() => {
    if (schemaArray.length === 0) return;
    const serialized = JSON.stringify(props.initialData || {});
    if (serialized === lastInitialDataSerializedRef.current) return;
    lastInitialDataSerializedRef.current = serialized;
    // Always overwrite with fresh initialData (don't merge — initialData is the source of truth)
    setInitialValues(props.initialData || {});
  }, [props.initialData, props.schema, schemaArray.length]);

  // Track changes to props
  useEffect(() => {
    propChangeCount.current += 1;
  }, [props]);

  // useEffect(() => {
  //   if (props.initialData) {
  //     setInitialValues(props.initialData)
  //   }
  // }, [props.initialData, props.schema])

  // useLayoutEffect(() => {
  //   if (props.containerWidth && props.columns > 1)
  //     calculateColumns(props.containerWidth);
  // }, [])

  // const calculateColumns = (containerWidth = 420) => {
  //   if(props.columns && containerWidth > 350){
  //     //Try to force columns unless container width is less than 350
  //     setColumns(props.columns);
  //     return;
  //   }
  //   const maxColumnWidth = 420; // Maximum width for each column
  //   if (Platform.OS === 'web') {
  //     setColumns(Math.floor(containerWidth / maxColumnWidth));
  //   } else {
  //     setColumns(1);
  //   }
  // };
  const RenderRows = React.memo<{
    otherVals: any;
    hideLabel?: boolean;
    blackText?: any;
    schemaArray: any[];
    nextFieldFunction?: any;
    setInputRef?: any;
    columns?: number;
    formInstanceId?: string;
    readOnly?: boolean;
    inlineFields?: boolean;
    insideBottomSheet?: boolean;
    fieldStyle?: any;
    labelStyle?: any;
  }>(({ otherVals = {}, hideLabel, blackText, schemaArray, nextFieldFunction, setInputRef, columns = 1, formInstanceId, readOnly = false, inlineFields = false, insideBottomSheet = false, fieldStyle, labelStyle }) => {
    const delegateScrollToParent = Platform.OS !== 'web' && props.scrollEnabled === false;

    const uniqueSchemaArray = useMemo(() => {
      // Use the input array length for math
      const totalCount = schemaArray.length;

      return schemaArray.map((item, index) => {
        const calculatedZIndex = (totalCount - index) * 100; // Multiply by 100 for safety buffer

        // LOGGING: Check your console to see the stacking order

        return {
          ...item,
          key: item.key || item.title,
          htmlId: formInstanceId ? `${formInstanceId}-${item.key || item.title}` : (item.key || item.title),
          // We attach the zIndex to the item so Row.tsx can use it
          computedZIndex: calculatedZIndex
        };
      });
    }, [schemaArray, formInstanceId]);

    // --- CASE 1: INLINE FIELDS (Multi-column wrap) ---
    if (inlineFields) {
      return useMemo(() => {
        return (
          <XStack
            flexWrap="wrap"
            gap={20}
            padding={5}
            width="100%"
            alignItems="flex-start"
            style={{ zIndex: 1 }}
          >
            {uniqueSchemaArray.map((item, index) => {
              const fieldName = item.key || item.title;
              const htmlId = item.htmlId || fieldName;
              const fieldType = item.ref || item.validation?.type || item.type || item.content?.type;
              const isObjectOrGroup = fieldType === 'object' || fieldType === 'group' || fieldType === 'Object';
              const isWeb = Platform.OS === 'web';

              return (
                <View
                  key={htmlId}
                  style={[
                    isObjectOrGroup
                      ? { minWidth: '100%', width: '100%' }
                      : isWeb
                        ? { maxWidth: 400, minWidth: 280, width: '100%', flexGrow: 0, flexShrink: 0, flexBasis: 'auto' }
                        : { maxWidth: 400, flex: 1, minWidth: 0, width: '100%' },
                    {
                      // FIX: Descending zIndex so earlier items float over later items
                      zIndex: uniqueSchemaArray.length - index,
                      elevation: uniqueSchemaArray.length - index,
                      position: 'relative',
                      alignSelf: isWeb ? 'stretch' : undefined,
                    }
                  ]}
                >
                  <Row
                    otherVals={otherVals}
                    column
                    hideLabel={hideLabel}
                    blackText={blackText}
                    allItems={uniqueSchemaArray}
                    items={[item]}
                    index={index}
                    totalRowsCount={uniqueSchemaArray.length}
                    nextFieldFunction={nextFieldFunction}
                    setInputRef={setInputRef}
                    readOnly={readOnly}
                    inlineFields={inlineFields}
                    insideBottomSheet={insideBottomSheet}
                    delegateScrollToParent={delegateScrollToParent}
                    fieldStyle={fieldStyle}
                    labelStyle={labelStyle}
                  />
                </View>
              );
            })}
          </XStack>
        );
      }, [uniqueSchemaArray, blackText, otherVals, hideLabel, nextFieldFunction, setInputRef, readOnly, inlineFields, insideBottomSheet, delegateScrollToParent]);
    }

    // Calculate actual number of rows
    const totalRows = Math.ceil(uniqueSchemaArray.length / columns);

    // --- CASE 2: SINGLE COLUMN (The standard list) ---
    if (columns <= 1) {
      return useMemo(() => {
        // Web: one field per stack item so siblings stay in document flow (avoids z-index overlap in narrow panels).
        if (Platform.OS === 'web' && !inlineFields) {
          return (
            <View
              {...webFormClass('ss-schema-form-fields')}
              style={{
                zIndex: 1,
                position: 'relative',
                width: '100%',
                flexShrink: 0,
                alignSelf: 'stretch',
              }}
            >
              {uniqueSchemaArray.map((item, index) => (
                <View
                  key={item.htmlId || item.key || item.title}
                  {...webFormClass('ss-schema-form-field-item')}
                  style={{
                    width: '100%',
                    flexShrink: 0,
                    flexGrow: 0,
                    alignSelf: 'stretch',
                    marginBottom: index < uniqueSchemaArray.length - 1 ? 24 : 0,
                    zIndex: uniqueSchemaArray.length - index,
                    elevation: uniqueSchemaArray.length - index,
                  }}
                >
                  <Row
                    otherVals={otherVals}
                    column
                    hideLabel={hideLabel}
                    blackText={blackText}
                    allItems={uniqueSchemaArray}
                    items={[item]}
                    index={index}
                    totalRowsCount={uniqueSchemaArray.length}
                    nextFieldFunction={nextFieldFunction}
                    setInputRef={setInputRef}
                    readOnly={readOnly}
                    inlineFields={inlineFields}
                    insideBottomSheet={insideBottomSheet}
                    delegateScrollToParent={delegateScrollToParent}
                    fieldStyle={fieldStyle}
                    labelStyle={labelStyle}
                  />
                </View>
              ))}
            </View>
          );
        }

        // We render one Row that manages its own internal item z-indexing
        return (
          <View
            {...webFormClass('ss-schema-form-fields')}
            style={{
              zIndex: 1,
              position: 'relative',
              ...(Platform.OS === 'web'
                ? { width: '100%', flexShrink: 0, alignSelf: 'stretch' }
                : { width: '100%', minWidth: 0, maxWidth: '100%', alignSelf: 'stretch' }),
            }}
          >
            <Row
              otherVals={otherVals}
              column
              hideLabel={hideLabel}
              blackText={blackText}
              allItems={uniqueSchemaArray}
              items={uniqueSchemaArray}
              index={0}
              totalRowsCount={uniqueSchemaArray.length} // Pass length to allow internal z-index math
              nextFieldFunction={nextFieldFunction}
              setInputRef={setInputRef}
              readOnly={readOnly}
              inlineFields={inlineFields}
              insideBottomSheet={insideBottomSheet}
              delegateScrollToParent={delegateScrollToParent}
              fieldStyle={fieldStyle}
              labelStyle={labelStyle}
            />
          </View>
        );
      }, [uniqueSchemaArray, blackText, otherVals, hideLabel, nextFieldFunction, setInputRef, totalRows, insideBottomSheet, delegateScrollToParent, fieldStyle, labelStyle, readOnly, inlineFields]);
    }

    // --- CASE 3: EXPLICIT GRID COLUMNS ---
    const rows = [];
    for (let i = 0; i < uniqueSchemaArray?.length; i += columns) {
      const rowNumber = Math.floor(i / columns);
      rows.push(
        <View
          key={i}
          style={{
            // FIX: Earlier rows must have higher zIndex than later rows
            zIndex: totalRows - rowNumber,
            elevation: totalRows - rowNumber,
            position: 'relative',
            ...(Platform.OS === 'web'
              ? { width: '100%', flexShrink: 0, alignSelf: 'stretch' }
              : {}),
          }}
        >
          <Row
            otherVals={otherVals}
            column={false}
            hideLabel={hideLabel}
            blackText={blackText}
            allItems={uniqueSchemaArray}
            items={uniqueSchemaArray.slice(i, i + columns)}
            index={rowNumber}
            totalRowsCount={totalRows}
            nextFieldFunction={undefined}
            setInputRef={undefined}
            readOnly={readOnly}
            inlineFields={inlineFields}
            insideBottomSheet={insideBottomSheet}
            delegateScrollToParent={delegateScrollToParent}
            fieldStyle={fieldStyle}
            labelStyle={labelStyle}
          />
        </View>
      );
    }
    return rows;
  });

  // Staged Row Renderer - Optimized to prevent input focus loss
  const RenderStagedRows = React.memo(({
    otherVals = {},
    hideLabel,
    blackText,
    visibleFields,
    currentIndex,
    nextFieldFunction,
    setInputRef,
    values,
    validateForm,
    readOnly
  }: {
    otherVals: any;
    hideLabel?: boolean;
    blackText?: boolean;
    visibleFields: any[];
    currentIndex: number;
    nextFieldFunction?: any;
    setInputRef?: any;
    values?: any;
    validateForm?: any;
    readOnly?: boolean;
  }) => {
    const delegateScrollToParent = Platform.OS !== 'web' && props.scrollEnabled === false;
    const theme = themeSelector();
    // Track full visibleFields from parent so conditional show/hide updates (same length + keys edge case).
    const memoizedVisibleFields = useMemo(() => visibleFields, [visibleFields]);

    if (!memoizedVisibleFields || memoizedVisibleFields.length === 0 || currentIndex >= memoizedVisibleFields.length) {
      return null;
    }

    const currentField = memoizedVisibleFields[currentIndex];
    const currentFieldArray = useMemo(() => [currentField], [currentField?.title || currentField?.key]);

    // Create a stable key that doesn't change during typing - use field title/key instead of index
    const stableKey = useMemo(() => `staged-${currentField?.title || currentField?.key || 'field'}`, [currentField?.title, currentField?.key]);

    // Memoize the Row component with stable dependencies
    const MemoizedRow = useMemo(() => (
      <Row
        key={stableKey}
        otherVals={otherVals}
        column={true}
        hideLabel={props.hideLabel || false}
        blackText={blackText}
        allItems={memoizedVisibleFields}
        items={currentFieldArray}
        index={currentIndex}
        totalRowsCount={memoizedVisibleFields.length}
        nextFieldFunction={nextFieldFunction}
        setInputRef={setInputRef}
        readOnly={readOnly}
        insideBottomSheet={props.embeddedInBottomSheetScroll}
        delegateScrollToParent={delegateScrollToParent}
      />
    ), [stableKey, otherVals, hideLabel, blackText, memoizedVisibleFields, currentFieldArray, currentIndex, nextFieldFunction, setInputRef, readOnly, props.embeddedInBottomSheetScroll, delegateScrollToParent]);

    return (
      <View key={stableKey} style={{ minWidth: 600, width: '100%' }}>
        {/* Question Counter */}
        <View style={{
          alignItems: 'center',
          marginBottom: 20,
          paddingVertical: 10,
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderRadius: 12,
          marginHorizontal: 16
        }}>
          <Text style={{
            fontSize: responsiveFontSize(14),
            fontWeight: '600',
            color: theme? '#3b82f6' :"white",
          }}>
            Question {currentIndex + 1} of {memoizedVisibleFields.length}
          </Text>
        </View>

        {/* Progress Indicator Dots */}
        <View style={{ alignItems: 'center', marginBottom: 20 }}>
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4
          }}>
            {memoizedVisibleFields.map((_, index) => (
              <View
                key={index}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: index === currentIndex ? '#3b82f6' :
                    index < currentIndex ? '#10b981' : '#e5e7eb'
                }}
              />
            ))}
          </View>
        </View>

        {/* Question Card */}
        <View style={{
          backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
          borderRadius: 16,
          marginHorizontal: 16,
          marginBottom: 20,
          padding: 20,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 8,
          elevation: 3
        }}>
          {/* Question Title */}
          {currentField?.label && (
            <Text style={{
              fontSize: responsiveFontSize(16),
              fontWeight: '600',
              color: '#1a1a1a',
              marginBottom: 8,
              textAlign: 'center'
            }}>
              {currentField.label}
            </Text>
          )}

          {/* Question Description */}
          {currentField?.description && (
            <Text style={{
              fontSize: responsiveFontSize(12),
              color: '#666',
              textAlign: 'center',
              marginBottom: 16,
              lineHeight: responsiveFontSize(18)
            }}>
              {currentField.description}
            </Text>
          )}

          {/* Input Field - Using memoized Row to prevent re-mounting */}
          {MemoizedRow}
        </View>

        {/* Navigation Controls */}
        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingHorizontal: 20,
          paddingVertical: 16,
          backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
          borderRadius: 16,
          marginHorizontal: 16,
          marginBottom: 10,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          zIndex:-1,
          shadowRadius: 8,
          elevation: 3
        }}>
          {/* Previous Button */}
          <View style={{ flex: 1, alignItems: 'flex-start' }}>
            {currentIndex > 0 && (
              <TouchableOpacity
                onPress={() => goToPreviousQuestion()}
                activeOpacity={0.7}
                style={{
                  backgroundColor: '#6b7280',
                  paddingHorizontal: 16,
                  paddingVertical: 10,
                  borderRadius: 8,
                  flexDirection: 'row',
                  alignItems: 'center'
                }}
              >
                <Text style={{
                  color: 'white',
                  fontSize: responsiveFontSize(12),
                  fontWeight: '600'
                }}>
                  {t('← Previous')}
                </Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Spacer for center alignment */}
          <View style={{ flex: 2, alignItems: 'center' }}>
            <View />
          </View>

          {/* Next Button */}
          <View style={{ flex: 1, alignItems: 'flex-end' }}>
            {currentIndex < memoizedVisibleFields.length - 1 && (
              <TouchableOpacity
                onPress={() => goToNextQuestion(memoizedVisibleFields, values, validateForm)}
                activeOpacity={0.7}
                style={{
                  backgroundColor: '#3b82f6',
                  paddingHorizontal: 16,
                  paddingVertical: 10,
                  borderRadius: 8,
                  flexDirection: 'row',
                  alignItems: 'center'
                }}
              >
                <Text style={{
                  color: 'white',
                  fontSize: responsiveFontSize(12),
                  fontWeight: '600'
                }}>
                  {t('Next →')}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    );
  });

  useEffect(() => {
    const processSchema = () => {
      return new Promise((resolve, reject) => {
//Rearrange schema array if it contains images or image to bring that to the top: 
        let moddedSchema = [];

        if(!props.schema) {
          console.log('⚠️  No schema provided');
          resolve(initialValues);
          return;
        }

        // CRITICAL: Check if we've already processed this exact schema
        // Use schema reference comparison to prevent re-processing on re-renders
        const schemaKey = JSON.stringify(props.schema);
        if (schemaProcessedRef.current && processedSchemaRef.current === schemaKey) {
          console.log('⚠️  Schema already processed - skipping re-initialization');
          resolve(initialValues);
          return;
        }

        //Add check to see if schemaArray already exists
        if (schemaArray.length > 0 && schemaProcessedRef.current) {
          console.log('⚠️  Schema array already populated - skipping re-initialization');
          resolve(initialValues);
          return;
        }

        const { fields: normalizedFields, initialValues: nestedInitVals } =
          normalizeSchemaEntries(props.schema as Record<string, unknown>, {
            nullify: props.nullify,
          });

        // Drop computed lookup keys from initial values (resolved at read time).
        const initVals: Record<string, unknown> = { ...nestedInitVals };
        const newSchemaArray = normalizedFields.filter((field) => {
          if (isSchemaKeyNullified(field.key, props.nullify)) return false;
          if (isComputedLookupField(field)) {
            delete initVals[field.key];
          }
          return true;
        });

        // Set schema array first
        setSchemaArray(newSchemaArray);

        // Merge with initialData if provided and retainInitialValues is true
        const finalInitialValues = props.retainInitialValues && props.initialData
          ? { ...initVals, ...props.initialData }
          : props.initialData || initVals;

        // Set initial values after schema array
        setInitialValues(finalInitialValues);

        // Mark schema as processed to prevent re-initialization on re-renders
        schemaProcessedRef.current = true;
        processedSchemaRef.current = JSON.stringify(props.schema);
        // So the initialData sync effect doesn't overwrite on first run
        lastInitialDataSerializedRef.current = JSON.stringify(props.initialData || {});

        resolve(finalInitialValues);
      });
    };

    const initializeSchema = async () => {
      setLoading(true);
      try {
        await processSchema();
      }
      catch (error) {
        console.error('❌ Error initializing schema:', error);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      }
    };

    // Only initialize if schema hasn't been processed yet
    if (!schemaProcessedRef.current || processedSchemaRef.current !== JSON.stringify(props.schema)) {
      initializeSchema();
    }
  }, [props.schema]);

  // Generate Yup validation schema - memoized to prevent unnecessary regeneration
  // NOTE: This schema includes ALL fields, but we filter validation errors for hidden fields in onSubmit
  const validationSchema = useMemo(() => {
    const schema = generateYupValidation(schemaArray);
    return schema;
  }, [schemaArray]);
  
  // Create a conditional validation function that only validates visible fields
  // Formik will use this instead of validationSchema if both are provided
  // NOTE: Validation is submit-only for web touch devices (validateOnBlur=false, validateOnChange=false)
  const conditionalValidate = useCallback(async (values: any) => {
    const visibleFields = getVisibleFields(values || {});
    const visibleFieldNames = new Set(visibleFields.map(f => f.title || f.key));
    
    // Get all validation errors using the schema
    let allErrors: any = {};
    try {
      await validationSchema.validate(values, { abortEarly: false });
      // If validation passes, return empty errors
      allErrors = {};
    } catch (err: any) {
      // Extract errors from validation exception
      if (err.inner) {
        err.inner.forEach((error: any) => {
          if (error.path) {
            allErrors[error.path] = error.message;
          }
        });
      }
    }
    
    // Filter to only visible fields
    const visibleErrors: any = {};
    Object.keys(allErrors || {}).forEach(fieldName => {
      if (visibleFieldNames.has(fieldName)) {
        visibleErrors[fieldName] = allErrors[fieldName];
      }
    });
    
    // Return empty object if no errors, or errors object if there are errors
    return Object.keys(visibleErrors).length > 0 ? visibleErrors : undefined;
  }, [validationSchema, getVisibleFields]);

  // Display form errors
  const displayErrors = (errors) => {
    let errorString = '';
    Object.keys(errors).forEach((key) => {
      const value = errors[key];
      errorString += parseByDelimiter(key).toString() + ' ' + value + '\n';
    });
    return errorString;
  };

  // Focus on next field
  const handleFocusNextField = useCallback((nextField, index, length) => {
    const currentRef = inputRefs.current[nextField];
    if (currentRef && index < length - 1) {
      currentRef.current?.focus();
    } else {
      Keyboard.dismiss();
    }
  }, [inputRefs]);

  // Set input reference
  const setInputRef = (fieldName, ref) => {
    inputRefs.current[fieldName] = ref;
  };

  const validateForm = useCallback(async () => {
    const currentFormikRef = ref as React.MutableRefObject<FormikProps<any>>;
    if (currentFormikRef?.current) {
      return await currentFormikRef.current.validateForm();
    }
    return {};
  }, [ref]);

  // Don't wrap in useMemo - it causes remounts when dependencies change during initialization
  const theme = themeSelector();
  
  return (
    <View style={mergeWebFormRootStyle({
      backgroundColor: 'transparent',
      flex: props.embeddedInBottomSheetScroll || props.scrollEnabled === false ? 0 : 1,
      minHeight: Platform.OS !== 'web'
        ? (props.embeddedInBottomSheetScroll || props.scrollEnabled === false ? undefined : "80%")
        : undefined,
      width: '100%',
      maxWidth: '100%',
      minWidth: Platform.OS === 'web' ? '100%' : 0,
      alignSelf: "stretch",
      paddingBottom: props.addPadding ? 250 : undefined
    }, props.customStyling)}>
      {loading && (
        <View style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingVertical: 60,
          paddingHorizontal: 20,
          minHeight: 300
        }}>
          <View style={{
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            borderRadius: 16,
            backgroundColor: theme ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 41, 59, 0.9)',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 8,
            elevation: 3,
            minWidth: 200
          }}>
            <Spinner size="large" color={theme ? '#3b82f6' : '#60a5fa'} />
            <Text style={{
              marginTop: 20,
              fontSize: responsiveFontSize(14),
              fontWeight: '600',
              color: theme ? '#1f2937' : '#f9fafb',
              textAlign: 'center'
            }}>
              {t('Loading form fields...')}
            </Text>
            <Text style={{
              marginTop: 8,
              fontSize: responsiveFontSize(11),
              color: theme ? '#6b7280' : '#9ca3af',
              textAlign: 'center',
              maxWidth: 250
            }}>
              {t('Please wait while we prepare your form')}
            </Text>
          </View>
        </View>
      )}
      {!loading && schemaArray.length === 0 && (
        <View style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingVertical: 40,
          paddingHorizontal: 20,
        }}>
          <Text style={{
            fontSize: responsiveFontSize(13),
            fontWeight: '600',
            color: theme ? '#374151' : '#e5e7eb',
            textAlign: 'center',
          }}>
            {t('No form fields in this schema.')}
          </Text>
        </View>
      )}
      {!loading && schemaArray.length > 0 && (
        <Formik
          initialValues={initialValues}
          innerRef={ref}
          enableReinitialize={true}
          validateOnChange={false} // Disable validation on change to prevent re-renders that cause keyboard dismissal
          validateOnBlur={Platform.OS === 'web' && isTouchDeviceRef.current ? false : true} // Disable blur validation on web touch devices to prevent form remounting
          validateOnMount={false}
          validate={conditionalValidate} // Use conditional validation that filters hidden fields
          validationSchema={undefined} // Don't use validationSchema when we have validate function
          onSubmit={async (values, helpers) => {
          try {
            // Step 0: Validate form before submission
            const allValidationErrors = await validateForm();
            
            // CRITICAL: Filter out validation errors for hidden/conditional fields
            const visibleFields = getVisibleFields(values || {});
            const visibleFieldNames = new Set(visibleFields.map(f => f.title || f.key));
            
            const validationErrors: any = {};
            if (allValidationErrors) {
              Object.keys(allValidationErrors).forEach(fieldName => {
                if (visibleFieldNames.has(fieldName)) {
                  // Only include errors for visible fields
                  validationErrors[fieldName] = allValidationErrors[fieldName];
                } else {
                  console.log(`⏭️  Skipping validation error for hidden field: ${fieldName}`);
                }
              });
            }
            
            if (validationErrors && Object.keys(validationErrors).length > 0) {
              console.error("❌ [GenerateSchemaForm] VALIDATION FAILED - Blocking submission");
              console.error("📊 Validation errors (visible fields):", JSON.stringify(validationErrors, null, 2));
              helpers.setTouched(setNestedObjectValues<FormikTouched<FormValues>>(validationErrors, true));
              helpers.setStatus({ error: "Submission failed. Please try again." });
              console.log("⚠️ [GenerateSchemaForm] Submission BLOCKED due to validation errors");
              return;
            }
            
            // Step 1: Await the submission.
            if (!props.submitFunction) {
              helpers.setStatus({ error: "Submit function not configured" });
              return;
            }
            
            const res = await props.submitFunction({
              ...omitComputedLookupValues(values, schemaArray),
              ...props.extraValues,
            }, props.other ? combinedIds : id);

            // Step 2: Immediately set the success status.
            // This makes the button show its success state (✅) right away.
            helpers.setStatus({ success: true });

            // Step 2b: Sync form to submitted values so the form shows the latest saved data
            // (avoids stale display when callback triggers re-render before parent refetches)
            setInitialValues(values);
            helpers.resetForm({ values });

            if (props.extraCallBack && props.extraCallBackImmediate) {
              props.extraCallBack(res?.data);
            }

            // Step 3: Schedule all additional functions to run after a 3-second delay.
            // This prevents them from causing conflicting re-renders.
            setTimeout(() => {
              // --- All side-effects are now inside this delay ---
              if (props.extraFunction) {
                props.extraFunction(values);
              }
              if (props.callback) {
                props.callback.setState(prevState => !prevState);
              }
              if (props.sheetRef?.current) {
                props.sheetRef.current.snapToIndex?.(0);
              }
              if (props.extraCallBack && !props.extraCallBackImmediate) {
                props.extraCallBack(res?.data);
              }
              if (props.resetOnSubmit) {
                helpers.resetForm();
              }

              // Optional: Reset the button's status back to normal after the delay.
              helpers.setStatus(null);

            }, 3000); // 3-second delay

          } catch (error) {
            // --- On Failure ---
            console.error("Submission failed:", error);
            helpers.setStatus({ error: "Submission failed. Please try again." });

            // Clear the error message after a few seconds.
            setTimeout(() => {
              helpers.setStatus(null);
            }, 3000);
          }
        }}
      >
          {(formikProps) => (
            <FormBody
              formikProps={formikProps}
              props={props}
              schemaArray={schemaArray}
              formInstanceId={formInstanceId}
              currentQuestionIndex={currentQuestionIndex}
              setCurrentQuestionIndex={setCurrentQuestionIndex}
              endReachedTriggered={endReachedTriggered}
              handleFocusNextField={handleFocusNextField}
              setInputRef={setInputRef}
              getVisibleFields={getVisibleFields}
              RenderStagedRows={RenderStagedRows}
              RenderRows={RenderRows}
              isDevice={isDevice}
              isTouchDeviceRef={isTouchDeviceRef}
              shouldPreventBlurRef={shouldPreventBlurRef}
              focusTimeRef={focusTimeRef}
              columns={columns}
              inlineFields={props.inlineFields}
              fieldStyle={props.fieldStyle}
              labelStyle={props.labelStyle}
            />
          )}
      </Formik>
      )}
    </View>
  );
});


// Utility function to merge styles without border conflicts
const mergeButtonStyles = (baseStyle, customStyle) => {
  if (!customStyle) return baseStyle;

  const merged = { ...baseStyle, ...customStyle };

  // Handle borderRadius conflicts
  if (merged.borderRadius && (
    merged.borderTopLeftRadius ||
    merged.borderTopRightRadius ||
    merged.borderBottomLeftRadius ||
    merged.borderBottomRightRadius
  )) {
    delete merged.borderRadius;
  }

  // Remove any conflicting border properties
  if (merged.borderColor && (merged.borderTopColor || merged.borderRightColor || merged.borderBottomColor || merged.borderLeftColor)) {
    // Keep specific border colors and remove shorthand
    delete merged.borderColor;
  }

  // Handle borderWidth conflicts
  if (merged.borderWidth && (
    merged.borderTopWidth ||
    merged.borderRightWidth ||
    merged.borderBottomWidth ||
    merged.borderLeftWidth
  )) {
    delete merged.borderWidth;
  }

  // Handle borderStyle conflicts
  if (merged.borderStyle && (
    merged.borderTopStyle ||
    merged.borderRightStyle ||
    merged.borderBottomStyle ||
    merged.borderLeftStyle
  )) {
    delete merged.borderStyle;
  }

  return merged;
};

// This is an example of an animatable button component
const Button = ({
  onPress,
  buttonText,
  isLoading,
  isSuccess,
  icon,
  disabled,
  defaultColor = '#3b82f6',
  customStyle
}) => {

  return (
    <MotiPressable
      onPress={onPress}
      disabled={disabled || isLoading}
      style={mergeButtonStyles({
        paddingVertical: 14,
        paddingHorizontal: 32,
        borderRadius: 12,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        minHeight: 48,
        borderWidth: 0,
        borderStyle: 'solid',
      }, customStyle)}
      animate={{
        backgroundColor: isSuccess
          ? '#10b981'
          : isLoading
            ? '#9ca3af'
            : disabled
              ? '#d1d5db'
              : defaultColor,
        opacity: disabled ? 0.6 : 1,
        scale: isLoading ? 0.98 : 1,
      }}
      transition={{
        type: 'timing',
        duration: 200,
      }}
      from={{ scale: 1 }}
    >
      {isSuccess ? (
        <Animatable.View
          animation="bounceIn"
          duration={600}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
        >
          <Animatable.Text
            animation="bounceIn"
            duration={600}
            style={{ color: 'white', fontSize: responsiveFontSize(16) }}
          >
            ✅
          </Animatable.Text>
          <Text style={{ color: 'white', fontSize: responsiveFontSize(14), fontWeight: '600' }}>
            {t('Success')}
          </Text>
        </Animatable.View>
      ) : isLoading ? (
          <Animatable.View
            animation="fadeIn"
            iterationCount="infinite"
            duration={800}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
          >
            <Spinner size="small" color="white" />
            <Text style={{ color: 'white', fontSize: responsiveFontSize(14), fontWeight: '600' }}>
              {buttonText}
            </Text>
          </Animatable.View>
      ) : (
            <Animatable.View
              animation="fadeIn"
              duration={200}
              style={{ flexDirection: 'row', alignItems: 'center', gap: icon ? 8 : 0 }}
            >
              {icon && (
                typeof icon === 'string' ? (
                  <Text style={{ color: 'white', fontSize: responsiveFontSize(16) }}>{icon}</Text>
                ) : (
                  icon
                )
              )}
              <Text style={{
                color: 'white',
                fontSize: responsiveFontSize(14),
                fontWeight: '600',
                textAlign: 'center'
              }}>
                {buttonText}
              </Text>
            </Animatable.View>
      )}
    </MotiPressable>
  );
};


export default memo(GenerateSchemaForm);
